import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/hooks/useLanguage';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';

interface FormData {
  name: string;
  organization: string;
  email: string;
  phone: string;
  country: string;
  message: string;
  consent: boolean;
}

export const ContactForm = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    organization: '',
    email: '',
    phone: '',
    country: '',
    message: '',
    consent: false
  });
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = t.common.required;
    }

    if (!formData.email.trim()) {
      newErrors.email = t.common.required;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t.language === 'en' ? 'Invalid email format' : 'รูปแบบอีเมลไม่ถูกต้อง';
    }

    if (!formData.message.trim()) {
      newErrors.message = t.common.required;
    }

    if (!formData.consent) {
      newErrors.consent = t.common.required;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock success
      setSubmitStatus('success');
      setFormData({
        name: '',
        organization: '',
        email: '',
        phone: '',
        country: '',
        message: '',
        consent: false
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Success Message */}
      {submitStatus === 'success' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <p className="text-green-800">
            {t.language === 'en' 
              ? 'Thank you! Your message has been sent successfully.'
              : 'ขอบคุณ! ข้อความของคุณได้ถูกส่งเรียบร้อยแล้ว'
            }
          </p>
        </div>
      )}

      {/* Error Message */}
      {submitStatus === 'error' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <p className="text-red-800">
            {t.language === 'en' 
              ? 'Sorry, there was an error sending your message. Please try again.'
              : 'ขออภัย เกิดข้อผิดพลาดในการส่งข้อความของคุณ โปรดลองอีกครั้ง'
            }
          </p>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm font-medium text-gray-700">
            {t.contact.form.name} *
          </Label>
          <Input
            id="name"
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={errors.name ? 'border-red-500' : ''}
            placeholder={t.contact.form.name}
          />
          {errors.name && (
            <p className="text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        {/* Organization */}
        <div className="space-y-2">
          <Label htmlFor="organization" className="text-sm font-medium text-gray-700">
            {t.contact.form.organization}
          </Label>
          <Input
            id="organization"
            type="text"
            value={formData.organization}
            onChange={(e) => handleInputChange('organization', e.target.value)}
            placeholder={t.contact.form.organization}
          />
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-gray-700">
            {t.contact.form.email} *
          </Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={errors.email ? 'border-red-500' : ''}
            placeholder={t.contact.form.email}
          />
          {errors.email && (
            <p className="text-sm text-red-600">{errors.email}</p>
          )}
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
            {t.contact.form.phone}
          </Label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder={t.contact.form.phone}
          />
        </div>

        {/* Country */}
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="country" className="text-sm font-medium text-gray-700">
            {t.contact.form.country}
          </Label>
          <Input
            id="country"
            type="text"
            value={formData.country}
            onChange={(e) => handleInputChange('country', e.target.value)}
            placeholder={t.contact.form.country}
          />
        </div>
      </div>

      {/* Message */}
      <div className="space-y-2">
        <Label htmlFor="message" className="text-sm font-medium text-gray-700">
          {t.contact.form.message} *
        </Label>
        <Textarea
          id="message"
          value={formData.message}
          onChange={(e) => handleInputChange('message', e.target.value)}
          className={`min-h-[120px] ${errors.message ? 'border-red-500' : ''}`}
          placeholder={t.contact.form.message}
        />
        {errors.message && (
          <p className="text-sm text-red-600">{errors.message}</p>
        )}
      </div>

      {/* Consent */}
      <div className="flex items-start space-x-3">
        <Checkbox
          id="consent"
          checked={formData.consent}
          onCheckedChange={(checked) => handleInputChange('consent', checked as boolean)}
          className={errors.consent ? 'border-red-500' : ''}
        />
        <Label
          htmlFor="consent"
          className={`text-sm leading-relaxed cursor-pointer ${errors.consent ? 'text-red-600' : 'text-gray-700'}`}
        >
          {t.contact.form.consent} *
        </Label>
      </div>
      {errors.consent && (
        <p className="text-sm text-red-600 ml-6">{errors.consent}</p>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-primary hover:bg-primary/90 text-white py-3 text-lg font-semibold"
      >
        {isSubmitting ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            {t.common.loading}
          </>
        ) : (
          <>
            <Send className="w-5 h-5 mr-2" />
            {t.contact.form.submit}
          </>
        )}
      </Button>
    </form>
  );
};